import { Button, HStack, useDisclosure, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import {
  HookFormDropdownInput,
  type HookFormDropdownInputProps,
} from '$/components/core/Form/HookFormDropdownInput';
import {
  HookFormInput,
  type HookFormInputProps,
} from '$/components/core/Form/HookFormInput';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import {
  trackContentImpression,
  trackContentInteraction,
  trackEvent,
} from '$/logger';
import { HookFormDatePicker } from '$/pages/StudentsPage/components/HookFormDatePicker';
import { type SchoolClass } from '$/services/usecases/school/mapper/schoolClasses';
import { inviteStudent } from '$/services/usecases/school/students';
import { emailFormatRegex } from '$/utils/regexUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  schoolClassOptions: SchoolClass[];
}

type InviteStudentFormData = {
  email: string;
  firstName: string;
  lastName: string;
  schoolClassId: string;
  query: string;
  expirationDate?: string;
};

const Input = (props: HookFormInputProps<InviteStudentFormData>) => (
  <HookFormInput<InviteStudentFormData> {...props} />
);

const Dropdown = (props: HookFormDropdownInputProps<InviteStudentFormData>) => (
  <HookFormDropdownInput<InviteStudentFormData> {...props} />
);

export const InviteStudentModal: FC<Props> = ({
  isOpen,
  onClose,
  schoolClassOptions,
}) => {
  const { t } = useTranslation();
  const { cloudMessageTranslation } = useCloudMessageTranslation();
  const toast = useToast();
  const classDropdown = useDisclosure();

  const { mutate, isPending } = useMutation({
    mutationFn: inviteStudent,
    onSuccess: (data, variables) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }

      toast(
        t('school.schoolClasses.inviteStudent.toastTitle'),
        'success',
        t('school.schoolClasses.inviteStudent.toastDescription'),
      );

      trackEvent(
        'Schools',
        'NewStudent',
        variables.expirationDate
          ? Math.ceil(
              (new Date(variables.expirationDate).getTime() -
                new Date().getTime()) /
                (1000 * 60 * 60 * 24),
            ).toString()
          : undefined,
      );

      onCloseModal();
    },
  });

  const formMethods = useForm<InviteStudentFormData>({
    mode: 'onTouched',
  });

  const onSubmit = (data: InviteStudentFormData) => {
    trackEvent('Schools', 'InviteStudent');

    mutate({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      schoolClassId: data.schoolClassId,
      expirationDate: data.expirationDate,
    });
  };

  useEffect(() => {
    if (isOpen) {
      trackContentImpression('Students', 'New', 'Open');
    }
  }, [isOpen]);

  const onCloseModal = () => {
    onClose();
    formMethods.reset();
  };

  const onCancelModal = () => {
    trackContentInteraction('ClickButton', 'Students', 'New', 'Cancel');
    onCloseModal();
  };

  return (
    <FullModal
      isOpen={isOpen}
      onClose={onCancelModal}
      header={t('school.schoolClasses.inviteStudent.title')}
      size='xl'
      isScrollable={false}
    >
      <FormProvider {...formMethods}>
        <Stack as='form' gap='4' onSubmit={formMethods.handleSubmit(onSubmit)}>
          <HStack w='full'>
            <Input
              accessor='firstName'
              label={t('dashboard.authentication.firstNameLabel')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
            <Input
              accessor='lastName'
              label={t('dashboard.authentication.lastNameLabel')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
          </HStack>
          <Input
            accessor='email'
            label={t('dashboard.authentication.emailLabel')}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
              pattern: {
                value: emailFormatRegex,
                message: t(
                  'dashboard.authentication.errorMessages.wrongFormat',
                ),
              },
            }}
          />
          <Dropdown
            accessor='query'
            label={t('school.schoolClasses.inviteStudent.classDropdownLabel')}
            dropdownOpen={classDropdown.isOpen}
            placeholder={t(
              'school.schoolClasses.inviteStudent.classDropdownPlaceholder',
            )}
            toggleDropdown={classDropdown.onToggle}
            dropdownContent={() => (
              <Stack pt='2' pb='6px'>
                {schoolClassOptions?.map((schoolClass) => (
                  <Button
                    key={schoolClass.id}
                    px='4'
                    py='6px'
                    fontSize='sm'
                    fontWeight='normal'
                    textAlign='left'
                    _hover={{ bg: 'bodyBackground' }}
                    cursor='pointer'
                    onClick={() => {
                      formMethods.setValue('schoolClassId', schoolClass.id);
                      formMethods.setValue('query', schoolClass.name);
                      classDropdown.onClose();
                    }}
                    type='button'
                    variant='unstyled'
                  >
                    <HStack>
                      <Text>{schoolClass.name}</Text>
                    </HStack>
                  </Button>
                ))}
              </Stack>
            )}
          />
          <HookFormDatePicker
            accessor='expirationDate'
            label={t('school.students.expirationDateLabel')}
            minDate={new Date()}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
            }}
          />
          <HStack justify='space-between' w='full' mt='10'>
            <Button
              isDisabled={isPending}
              onClick={onCancelModal}
              type='button'
              variant='ghost'
            >
              {t('general.cancel')}
            </Button>
            <Button
              isDisabled={!formMethods.formState.isValid}
              isLoading={isPending}
              onClick={() => {
                trackContentInteraction(
                  'ClickButton',
                  'Students',
                  'New',
                  'NewStudent',
                );
              }}
              type='submit'
            >
              {t('school.schoolClasses.inviteStudent.inviteButton')}
            </Button>
          </HStack>
        </Stack>
      </FormProvider>
    </FullModal>
  );
};
